import {
  AfterViewInit,
  ApplicationRef,
  Component,
  createComponent,
  ElementRef,
  EnvironmentInjector,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, EMPTY, finalize, Observable, Subject, Subscription } from 'rxjs';
import {
  GlobalMessageService,
  GlobalMessageType,
  RoutingService,
  TranslationService,
  WindowRef,
} from '@spartacus/core';
import { RecaptchaV2CheckResponseService } from '../../services/recaptcha-v2-check-response.service';
import { LaunchDialogService } from '@spartacus/storefront';
import { RecaptchaComponent } from 'ng-recaptcha-2';
import { GgFirmwareVerificationService } from '../../services/gg-firmware-verification.service';
import { switchMap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

export interface GGFirmwareWSDTO {
  fileName: string;
  version: string;
  firmwareVersion: string;
  exportModel: string;
  customerModelInfo: string;
  url: string;
}

@Component({
  selector: 'gg-product-firmware-verification',
  templateUrl: './gg-firmware-verification.component.html',
  styleUrls: ['./gg-firmware-verification.component.scss'],
  standalone: false,
})
export class GGFirmwareVerificationComponent implements OnInit, AfterViewInit, OnDestroy {
  verificationForm!: FormGroup;
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.loading$.asObservable();
  isReCapthcaErrorVisible: boolean = false;
  data!: { extraId: string };
  protected recaptchaResponse: string | null = null;
  private subscriptions = new Subscription();
  results$: BehaviorSubject<GGFirmwareWSDTO[]> = new BehaviorSubject<GGFirmwareWSDTO[]>([]);
  firmwareImgUrl$: Subject<string> = new Subject();
  firmwarePdfUrl$: Subject<string> = new Subject();
  isError$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isFormSubmitted$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isNoResults$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  protected recaptchaInit$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  @ViewChild('recaptchaWrapper') recaptchaWrapper!: ElementRef;

  constructor(
    protected fb: FormBuilder,
    protected router: RoutingService,
    private globalMessageService: GlobalMessageService,
    private recaptchaV2CheckResponseService: RecaptchaV2CheckResponseService,
    private injector: EnvironmentInjector,
    private appRef: ApplicationRef,
    protected launchDialogService: LaunchDialogService,
    private firmwareVerificationService: GgFirmwareVerificationService,
    private translation: TranslationService,
    private window: WindowRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.recaptchaWrapper) return;

    const initialized = this.recaptchaInit$.getValue();

    if (!initialized) {
      const captchaRef = createComponent(RecaptchaComponent, {
        environmentInjector: this.injector,
      });

      this.subscriptions.add(
        captchaRef.instance.resolved.subscribe((value: string) => {
          this.recaptchaResponse = value;
        })
      );

      this.appRef.attachView(captchaRef.hostView);

      this.recaptchaWrapper.nativeElement.appendChild(captchaRef.location.nativeElement);

      this.recaptchaInit$.next(true);
    }
  }

  ngOnInit(): void {
    this.verificationForm = this.fb.group({
      extraId: [
        '',
        [
          Validators.required,
        ],
      ],
    });

    this.subscriptions.add(
      this.translation.translate('firmwareVerification.imgUrl').subscribe((imgUrl) => this.firmwareImgUrl$.next(imgUrl))
    );

    this.subscriptions.add(
      this.translation.translate('firmwareVerification.pdfUrl').subscribe((pdfUrl) => this.firmwarePdfUrl$.next(pdfUrl))
    );
  }

  showRecaptchaError(): void {
    this.globalMessageService.add({ key: 'common.formMessagesRecaptchaFailure' }, GlobalMessageType.MSG_TYPE_ERROR);
  }

  removeRecaptchaError(): void {
    this.isReCapthcaErrorVisible = false;
    this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_ERROR);
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    this.resetVariables();
    this.loading$.next(true);

    if (!this.recaptchaResponse) {
      this.isReCapthcaErrorVisible = true;
      this.showRecaptchaError();
      this.loading$.next(false);
      return;
    } else {
      if (this.isReCapthcaErrorVisible) {
        this.removeRecaptchaError();
      }
    }

    if (this.verificationForm.valid) {
      this.isFormSubmitted$.next(true);
      this.subscriptions.add(
        this.recaptchaV2CheckResponseService
          .checkResponse(this.recaptchaResponse)
          .pipe(
            switchMap((recaptchaOk) => {
              if (!recaptchaOk) {
                this.showRecaptchaError();
                return EMPTY;
              }

              this.data = this.verificationForm.value;
              return this.firmwareVerificationService.checkFirmware(this.data.extraId);
            }),
            finalize(() => {
              this.loading$.next(false);
            })
          )
          .subscribe({
            next: (results) => {
              this.isError$.next(false);
              this.results$.next(results);
              if (results.length > 0) {
                this.isNoResults$.next(false);
              } else {
                this.isNoResults$.next(true);
              }
              this.loading$.next(false);
            },
            error: () => {
              this.isError$.next(true);
              this.loading$.next(false);
            },
          })
      );
    } else {
      if (!this.verificationForm.get('extraId')?.valid) {
        this.verificationForm.get('extraId')?.markAsTouched();
        this.verificationForm.get('extraId')?.updateValueAndValidity();
      }

      this.data = { extraId: '' };
      this.loading$.next(false);
    }
  }

  resetVariables() {
    this.isFormSubmitted$.next(false);
    this.isError$.next(false);
    this.isNoResults$.next(false);
    this.data = { extraId: '' };
  }

  openFirmwareImage(imgUrl: string): void {
    this.launchDialogService.openDialogAndSubscribe('FIRMWARE_IMAGE_OPEN', undefined, imgUrl);
  }

  openPdfInNewWindow(pdfUrl: string): void {
    this.window.nativeWindow?.open(pdfUrl, '_blank');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.loading$.complete();
    this.resetVariables();
  }
}
